import { Component, inject, Input, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { fetchOrganizations } from 'src/app/accounts/store/accounts.actions';
import { AclService } from 'src/app/acl/acl.service';
import { OrganizationAclService } from 'src/app/organization/organization-acl.service';
import { ICreateAccount, EMPTY_ACCOUNT } from 'src/app/shared/account/create-account.helper';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { KeycloakService } from 'keycloak-angular';
import { LogService } from 'src/app/shared/log/log.service';
import { LogType } from 'src/app/shared/log/log.service.types';
import { ToastService } from 'src/app/toast/toast.service';
import { WhiteLabelService } from 'src/app/white-label/white-label.service';
import { Account } from 'src/app/shared/account/account.types';
import { ErrorResponseWrapper } from 'src/app/shared/http.types';
import { AccountsContextService } from '../../account-context.service';
import { AccountsService } from '../../accounts.service';
import { UpdateAccountPayload } from '../wizard.types';
import { filterStep2ServerErrors } from './account.helpers';
import Type = Account.Type;
import { PublisherShopHttpService } from 'src/app/http-services/publisher-shop-http.service';
import { AdvertiserHttpService } from 'src/app/http-services/advertiser-http.service';
import { filter, finalize } from 'rxjs/operators';
import { AdminOrganizationHttpService } from 'src/app/http-services/admin-organization-http.service';

@Component({
  selector: 'app-wizard-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss'],
})
export class AccountComponent implements OnDestroy {
  public formsCount = 4;
  public account$: BehaviorSubject<ICreateAccount> = new BehaviorSubject<ICreateAccount>(EMPTY_ACCOUNT);
  public currentStep$: BehaviorSubject<number> = new BehaviorSubject(1);
  public backendValidationErrorsListStep2$: BehaviorSubject<string[]> = new BehaviorSubject(<string[]>[]);
  public backendValidationErrorsListStep3$: BehaviorSubject<string[]> = new BehaviorSubject(<string[]>[]);
  public isCurrentFormValid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true
  );
  public createdAccount: ICreateAccount;
  public isCreatingAccount$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly whiteLabelService: WhiteLabelService;
  public readonly modal: NgbActiveModal | null;

  private unsubscribe: Subscription[] = [];
  private publisherShopHttpService: PublisherShopHttpService;
  private adminOrganizationHttpService: AdminOrganizationHttpService;
  private advertiserHttpService: AdvertiserHttpService;
  private router: Router;
  private accountsContextService: AccountsContextService;
  private accountsService: AccountsService;
  private toastService: ToastService;
  private translateService: TranslateService;
  private keycloakService: KeycloakService;
  private readonly aclService: AclService;
  private readonly logService: LogService;
  private readonly organizationAclService: OrganizationAclService;
  private readonly store: Store;

  constructor() {
    this.publisherShopHttpService = inject(PublisherShopHttpService);
    this.adminOrganizationHttpService = inject(AdminOrganizationHttpService);
    this.advertiserHttpService = inject(AdvertiserHttpService);
    this.whiteLabelService = inject(WhiteLabelService);
    this.router = inject(Router);
    this.accountsContextService = inject(AccountsContextService);
    this.accountsService = inject(AccountsService);
    this.toastService = inject(ToastService);
    this.translateService = inject(TranslateService);
    this.keycloakService = inject(KeycloakService);
    this.aclService = inject(AclService);
    this.logService = inject(LogService);
    this.store = inject(Store);
    this.organizationAclService = inject(OrganizationAclService);
    this.modal = inject(NgbActiveModal, { optional: true });

    const aclSubscription = this.organizationAclService.canGetOrganization$
      .pipe(
        filter(Boolean),
      )
      .subscribe(() => {
        this.store.dispatch(fetchOrganizations({
          rolePerOrganization: this.organizationAclService.rolePerOrg
        }));
      });

    this.unsubscribe.push(aclSubscription);
  }

  @Input()
  public set initialType(accountType: Account.Type | undefined) {
    if (accountType) {
      this.account$.next({ ...this.account$.getValue(), accountType })
    }
  }

  public updateAccount(payload: UpdateAccountPayload) {
    const currentAccount = this.account$.value;
    const updatedAccount = { ...currentAccount, ...payload.part };
    this.account$.next(updatedAccount);
    this.isCurrentFormValid$.next(payload.isFormValid);
  };

  public nextStep() {
    const nextStep = this.currentStep$.value + 1;
    if (nextStep > this.formsCount) {
      this.navigateToCreatedAccountDashboard();
    }

    if (this.shouldSubmitForm()) {
      this.account$.value.accountType == Account.Type.PUBLISHER ? this.submitShop() : this.submitAdvertiser();
    } else {
      this.currentStep$.next(nextStep);
    }
  }

  public handleSuccess = (response: ICreateAccount) => {
    this.createdAccount = response;
    this.currentStep$.next(4);
  }

  public handleError = (errorResponse: ErrorResponseWrapper) => {
    const { handledErrors, unhandledErrors } = filterStep2ServerErrors(errorResponse.errors);

    this.backendValidationErrorsListStep2$.next(
      handledErrors.map((a: { message: any; }) => a.message)
    )
    this.backendValidationErrorsListStep3$.next(
      unhandledErrors.map((a: { message: any; }) => a.message)
    )
    if (handledErrors.length) {
      this.currentStep$.next(2);
    }

    this.logService.logComponentError(new Error('Create account error'), LogType.ACCOUNT, AccountComponent.name, {
      code: errorResponse.code,
      errors: errorResponse.errors,
    });

    this.toastService.error(
      this.translateService.instant('WIZARD.SHOP.ERROR_MESSAGE')
    );
  }

  public prevStep() {
    const prevStep = this.currentStep$.value - 1;
    if (prevStep === 0) {
      return;
    }
    this.currentStep$.next(prevStep);
  }

  public ngOnDestroy() {
    this.unsubscribe.forEach(sb => sb.unsubscribe());
  }

  private submitShop() {
    const { logo, ...value } = this.account$.value;

    if (logo?.file) {
      let uploadLogoSubscription = this.publisherShopHttpService
        .uploadLogo(logo?.file, logo?.params?.name)
        .subscribe(({ data }) => {
          this.createShopFn({ ...value, logoId: data });
        });
      this.unsubscribe.push(uploadLogoSubscription);
    } else {
      this.createShopFn({ ...value, logoId: '' });
    }
  }

  private createShopFn = (value: ICreateAccount) => {
    this.isCreatingAccount$.next(true);
    const data = this.adjustDataBeforeSaving(value);
    const service = value?.organizationId ? this.adminOrganizationHttpService : this.publisherShopHttpService;

    let submitShopSubscription = service
      .createShop(data)
      .pipe(
        finalize(() => this.isCreatingAccount$.next(false)),
      )
      .subscribe(async resp => {
        this.handleSuccess({ ...resp, accountType: Type.PUBLISHER });

        // Token refresh needed
        const isRefreshed = await this.keycloakService.updateToken(-1);
        if (isRefreshed) {
          this.accountsService.fetchPublishers();
          this.aclService.fetchAcl();
        }
      }, this.handleError);
    this.unsubscribe.push(submitShopSubscription);
  }

  private submitAdvertiser() {
    this.isCreatingAccount$.next(true);

    const data = {
      ...this.account$.value,
      ...(this.whiteLabelService.isWhiteLabel ? {
        whiteLabel: this.whiteLabelService.dataSnapshot.hash
      }: {})
    };

    const service = data?.organizationId ? this.adminOrganizationHttpService : this.advertiserHttpService;

    let submitAdvertiserSubscription = service
      .createAdvertiser(data)
      .pipe(
        finalize(() => this.isCreatingAccount$.next(false)),
      )
      .subscribe(async resp => {
        this.handleSuccess({ ...resp, accountType: Type.ADVERTISER });

        // Token refresh needed
        const isRefreshed = await this.keycloakService.updateToken(-1);
        if (isRefreshed) {
          this.accountsService.fetchAdvertisers();
          this.aclService.fetchAcl();
        }
      }, this.handleError);
    this.unsubscribe.push(submitAdvertiserSubscription);
  }

  private shouldSubmitForm() {
    return this.currentStep$.value === 3;
  }

  private navigateToCreatedAccountDashboard() {
    this.router.navigateByUrl(
      this.accountsContextService.getAccountDashboardRoute(this.createdAccount)
    );
    this.modal?.close();
  }

  private adjustDataBeforeSaving(data: ICreateAccount): ICreateAccount {
    const adjustedData = Object.keys(data).reduce((acc, key) => {
      const value = data[key as keyof ICreateAccount];

      return {
        ...acc,
        [key]: typeof value === 'string' ? value?.trim() : value,
      };
    }, {} as ICreateAccount);

    return adjustedData;
  }
}
